import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import * as turf from '@turf/turf';
import api from '../api';
import CompanySetupForm from './CompanySetupForm';
import 'mapbox-gl/dist/mapbox-gl.css';
import './DataCoveragePanel.css';

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN || 'pk.eyJ1IjoibWljaGFlbC1ydCIsImEiOiJjbTFmY2gxZGYydjBhMmptejRkMXY3NnE4In0.HLgD2rXqws3hQ0Wr7X7PQw';

const FILL_LAYER_ID = 'coverage-zips-fill';
const OUTLINE_LAYER_ID = 'coverage-zips-outline';
const SOURCE_ID = 'coverage-zips-source';
const COVERAGE_STATUS_ACTIVE = 'active';
const COVERAGE_STATUS_DEMO = 'demo';
const COVERAGE_STATUS_INACTIVE = 'inactive';
const ZIP_EXPR = [
  'coalesce',
  ['get', 'GEOID20'],
  ['get', 'ZCTA5CE20'],
  ['get', 'ZIP'],
  ['get', 'zip'],
  ['to-string', ['get', 'zip_code']],
];
const ZIP_KEYS = ['GEOID20', 'ZCTA5CE20', 'ZIP', 'zip', 'zip_code'];

function sortZipCodes(values = []) {
  return [...new Set(values.filter(Boolean))].sort((left, right) => left.localeCompare(right));
}

function normalizeZip(rawValue) {
  const digits = String(rawValue ?? '').replace(/\D/g, '').slice(0, 5);
  return digits.length === 5 ? digits : '';
}

function getZipFromFeature(feature) {
  if (!feature?.properties) {
    return '';
  }
  for (const key of ZIP_KEYS) {
    const zipCode = normalizeZip(feature.properties[key]);
    if (zipCode) {
      return zipCode;
    }
  }
  return '';
}

function maxZoomForZipCount(zipCount) {
  if (zipCount <= 1) return 10.8;
  if (zipCount <= 4) return 9.8;
  if (zipCount <= 10) return 9.0;
  if (zipCount <= 24) return 8.2;
  if (zipCount <= 60) return 7.2;
  return 6.4;
}

function getFocusSubset(contexts) {
  if (contexts.length <= 6) {
    return contexts;
  }

  const center = contexts.reduce((accumulator, context) => ({
    latitude: accumulator.latitude + Number(context.latitude),
    longitude: accumulator.longitude + Number(context.longitude),
  }), { latitude: 0, longitude: 0 });
  center.latitude /= contexts.length;
  center.longitude /= contexts.length;

  const keepCount = Math.max(4, Math.ceil(contexts.length * 0.85));
  return [...contexts]
    .sort((left, right) => {
      const leftDistance = Math.hypot(Number(left.longitude) - center.longitude, Number(left.latitude) - center.latitude);
      const rightDistance = Math.hypot(Number(right.longitude) - center.longitude, Number(right.latitude) - center.latitude);
      return leftDistance - rightDistance;
    })
    .slice(0, keepCount);
}

function getCoverageStatusLabel(status) {
  if (status === COVERAGE_STATUS_ACTIVE) return 'Active';
  if (status === COVERAGE_STATUS_DEMO) return 'Demo';
  return 'Inactive';
}

function normalizeEmail(value) {
  const trimmed = String(value ?? '').trim().toLowerCase();
  return trimmed.includes('@') ? trimmed : '';
}

function getClientIdFromCoverageState(coverageState) {
  const actorUserId = String(coverageState?.actor_user_id || '');
  const dataUserId = String(coverageState?.data_user_id || '');
  return actorUserId && dataUserId && actorUserId !== dataUserId ? dataUserId : '';
}

export default function DataCoveragePanel({
  coverageState,
  selectedZip,
  onSelectZip,
  onCoverageUpdate,
  onCompanySetupComplete,
  // onSeatRequest — reserved for future seat management in review step
}) {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const lassoPointsRef = useRef([]);
  const lassoDrawingRef = useRef(false);
  const lassoActiveRef = useRef(false);
  const [lassoActive, setLassoActive] = useState(false);
  const [mapConfig, setMapConfig] = useState(null);
  const [mapReady, setMapReady] = useState(false);
  const [mapError, setMapError] = useState('');
  const [searchZip, setSearchZip] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [pendingAdds, setPendingAdds] = useState([]);
  const [pendingRemovals, setPendingRemovals] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState('select');
  const [requestState, setRequestState] = useState('idle'); // idle | submitting | complete
  const [requestNote, setRequestNote] = useState('');
  const [requestResult, setRequestResult] = useState(null);
  const [isSwitchingCompany, setIsSwitchingCompany] = useState(false);
  const [memberForm, setMemberForm] = useState({ email: '', role: 'member' });
  const [pendingMemberAdds, setPendingMemberAdds] = useState([]);
  const [pendingMemberRemovals, setPendingMemberRemovals] = useState([]);
  const [memberError, setMemberError] = useState('');
  const [memberActionKey, setMemberActionKey] = useState('');
  const [showAllZips, setShowAllZips] = useState(false);
  const [showTeamSection, setShowTeamSection] = useState(false);
  const companyZipCodesRef = useRef([]);
  const canInteractRef = useRef(false);
  const accessibleZipCodesRef = useRef([]);
  const autoAddedDemoForCompanyRef = useRef('');

  const companyZipStatusMap = useMemo(
    () => coverageState?.companyZipStatusMap || {},
    [coverageState]
  );
  const companyZipCodesByStatus = useMemo(
    () => coverageState?.companyZipCodesByStatus || {},
    [coverageState]
  );
  const companyZipCodes = useMemo(
    () => sortZipCodes(companyZipCodesByStatus.active || coverageState?.companyZipCodes || []),
    [companyZipCodesByStatus, coverageState]
  );
  const demoZipCodes = useMemo(
    () => sortZipCodes(companyZipCodesByStatus.demo || []),
    [companyZipCodesByStatus]
  );
  const inactiveZipCodes = useMemo(
    () => sortZipCodes(companyZipCodesByStatus.inactive || []),
    [companyZipCodesByStatus]
  );
  const allCompanyZipCodes = useMemo(
    () => sortZipCodes([...companyZipCodes, ...demoZipCodes, ...inactiveZipCodes]),
    [companyZipCodes, demoZipCodes, inactiveZipCodes]
  );

  const projectedZipCodes = useMemo(() => {
    const active = new Set(companyZipCodes);
    pendingRemovals.forEach((zipCode) => active.delete(zipCode));
    pendingAdds.forEach((zipCode) => active.add(zipCode));
    return sortZipCodes(Array.from(active));
  }, [companyZipCodes, pendingAdds, pendingRemovals]);

  const companyZipContextMap = useMemo(
    () => coverageState?.companyZipContextMap || {},
    [coverageState]
  );
  const accessibleZipCodes = useMemo(
    () => coverageState?.accessibleZipCodes || [],
    [coverageState]
  );
  const clientId = useMemo(
    () => getClientIdFromCoverageState(coverageState),
    [coverageState]
  );
  const companyMemberships = useMemo(
    () => coverageState?.company_memberships || [],
    [coverageState]
  );
  const selectedCompanyId = coverageState?.selected_company_id || coverageState?.company_id || '';
  const companyMembers = useMemo(
    () => coverageState?.company_members || [],
    [coverageState]
  );
  const visibleCompanyMembers = useMemo(
    () => companyMembers.filter((member) => !pendingMemberRemovals.some((pendingMember) => pendingMember.id === member.id)),
    [companyMembers, pendingMemberRemovals]
  );
  const canEditCoverage = Boolean(coverageState?.can_edit_coverage);
  const canManageCompanyMembers = Boolean(coverageState?.can_manage_company_members);
  const hasCompanyMembership = Boolean(coverageState?.has_company_membership);
  const isSelfContext = String(coverageState?.actor_user_id || '') === String(coverageState?.data_user_id || '');
  const hasPendingChanges =
    pendingAdds.length > 0
    || pendingRemovals.length > 0
    || pendingMemberAdds.length > 0
    || pendingMemberRemovals.length > 0;
  const canInteract = canEditCoverage;
  const isViewOnly = !canInteract;
  const needsCompanySetup = !hasCompanyMembership && isSelfContext;
  const currentCompanyMemberCount = Math.max(
    Number(coverageState?.company_member_count || companyMembers.length || 0),
    hasCompanyMembership ? 1 : 0
  );
  const projectedCompanyMemberCount = Math.max(
    visibleCompanyMembers.length + pendingMemberAdds.length,
    hasCompanyMembership ? 1 : 0
  );

  useEffect(() => {
    companyZipCodesRef.current = companyZipCodes;
  }, [companyZipCodes]);

  useEffect(() => {
    canInteractRef.current = canInteract;
  }, [canInteract]);

  useEffect(() => {
    accessibleZipCodesRef.current = accessibleZipCodes;
  }, [accessibleZipCodes]);

  useEffect(() => {
    setPendingAdds([]);
    setPendingRemovals([]);
    setPendingMemberAdds([]);
    setPendingMemberRemovals([]);
    setError('');
    setMemberError('');
    setStep('select');
    setRequestState('idle');
    setRequestNote('');
    setRequestResult(null);
    setMemberActionKey('');
    autoAddedDemoForCompanyRef.current = '';
  }, [selectedCompanyId]);

  // Auto-select demo ZIPs for activation on initial load / company switch
  useEffect(() => {
    if (demoZipCodes.length > 0 && autoAddedDemoForCompanyRef.current !== selectedCompanyId) {
      autoAddedDemoForCompanyRef.current = selectedCompanyId;
      setPendingAdds((current) => sortZipCodes([...new Set([...current, ...demoZipCodes])]));
    }
  }, [demoZipCodes, selectedCompanyId]);

  // ── Map config ──
  useEffect(() => {
    let isMounted = true;
    const loadMapConfig = async () => {
      try {
        const response = await api.get('/api/get-zip-code-map');
        if (isMounted) {
          setMapConfig(response.data);
        }
      } catch (requestError) {
        if (isMounted) {
          setMapError('Failed to load ZIP map configuration.');
        }
      }
    };
    loadMapConfig();
    return () => {
      isMounted = false;
    };
  }, []);

  // ── Map styling ──
  const updateMapStyling = useCallback(() => {
    const map = mapRef.current;
    if (!map || !map.getLayer(FILL_LAYER_ID)) {
      return;
    }

    const highlightedZip = selectedZip || '';
    map.setPaintProperty(FILL_LAYER_ID, 'fill-color', [
      'case',
      ['==', ZIP_EXPR, highlightedZip], '#0f766e',
      ['in', ZIP_EXPR, ['literal', pendingRemovals]], '#ef4444',
      ['in', ZIP_EXPR, ['literal', pendingAdds]], '#f59e0b',
      ['in', ZIP_EXPR, ['literal', companyZipCodes]], '#16a34a',
      ['in', ZIP_EXPR, ['literal', demoZipCodes]], '#0284c7',
      ['in', ZIP_EXPR, ['literal', inactiveZipCodes]], '#94a3b8',
      '#e7e2d9',
    ]);
    map.setPaintProperty(FILL_LAYER_ID, 'fill-opacity', [
      'case',
      ['==', ZIP_EXPR, highlightedZip], 0.92,
      ['in', ZIP_EXPR, ['literal', pendingRemovals]], 0.8,
      ['in', ZIP_EXPR, ['literal', pendingAdds]], 0.8,
      ['in', ZIP_EXPR, ['literal', companyZipCodes]], 0.72,
      ['in', ZIP_EXPR, ['literal', demoZipCodes]], 0.58,
      ['in', ZIP_EXPR, ['literal', inactiveZipCodes]], 0.34,
      0.18,
    ]);
    map.setPaintProperty(OUTLINE_LAYER_ID, 'line-color', [
      'case',
      ['==', ZIP_EXPR, highlightedZip], '#0f172a',
      ['in', ZIP_EXPR, ['literal', projectedZipCodes]], '#334155',
      ['in', ZIP_EXPR, ['literal', allCompanyZipCodes]], '#64748b',
      '#b7b0a6',
    ]);
    map.setPaintProperty(OUTLINE_LAYER_ID, 'line-width', [
      'case',
      ['==', ZIP_EXPR, highlightedZip], 2.2,
      ['in', ZIP_EXPR, ['literal', projectedZipCodes]], 1.1,
      ['in', ZIP_EXPR, ['literal', allCompanyZipCodes]], 0.8,
      0.45,
    ]);
  }, [
    allCompanyZipCodes,
    companyZipCodes,
    demoZipCodes,
    inactiveZipCodes,
    pendingAdds,
    pendingRemovals,
    projectedZipCodes,
    selectedZip,
  ]);
  const updateMapStylingRef = useRef(updateMapStyling);
  useEffect(() => {
    updateMapStylingRef.current = updateMapStyling;
  }, [updateMapStyling]);

  const focusOnZipContext = useCallback((context, zoom = 10) => {
    const map = mapRef.current;
    if (!map) {
      return false;
    }
    const longitude = Number(context?.longitude);
    const latitude = Number(context?.latitude);
    if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
      return false;
    }
    map.flyTo({
      center: [longitude, latitude],
      zoom,
      essential: true,
      duration: 700,
    });
    return true;
  }, []);

  const fitCoverageToContexts = useCallback((zipCodes) => {
    const map = mapRef.current;
    if (!map || !zipCodes?.length) {
      return false;
    }

    const contexts = getFocusSubset(
      zipCodes
        .map((zipCode) => {
          if (searchResult?.zip_code === zipCode) {
            return searchResult;
          }
          return companyZipContextMap[zipCode];
        })
        .filter((context) => Number.isFinite(Number(context?.longitude)) && Number.isFinite(Number(context?.latitude)))
    );

    if (!contexts.length) {
      return false;
    }

    if (contexts.length === 1) {
      return focusOnZipContext(contexts[0], maxZoomForZipCount(zipCodes.length));
    }

    const bounds = contexts.reduce((accumulator, context) => ({
      minLng: Math.min(accumulator.minLng, Number(context.longitude)),
      maxLng: Math.max(accumulator.maxLng, Number(context.longitude)),
      minLat: Math.min(accumulator.minLat, Number(context.latitude)),
      maxLat: Math.max(accumulator.maxLat, Number(context.latitude)),
    }), {
      minLng: Infinity,
      maxLng: -Infinity,
      minLat: Infinity,
      maxLat: -Infinity,
    });

    map.fitBounds(
      [
        [bounds.minLng, bounds.minLat],
        [bounds.maxLng, bounds.maxLat],
      ],
      {
        padding: 56,
        maxZoom: maxZoomForZipCount(contexts.length),
        essential: true,
        duration: 700,
      }
    );
    return true;
  }, [companyZipContextMap, focusOnZipContext, searchResult]);

  const toggleZipSelection = useCallback((zipCode) => {
    if (onSelectZip && accessibleZipCodesRef.current.includes(zipCode)) {
      onSelectZip(zipCode);
    }
    if (!canInteractRef.current) {
      return;
    }

    if (companyZipCodesRef.current.includes(zipCode)) {
      setPendingRemovals((current) => (
        current.includes(zipCode)
          ? current.filter((value) => value !== zipCode)
          : sortZipCodes([...current, zipCode])
      ));
      setPendingAdds((current) => current.filter((value) => value !== zipCode));
      return;
    }

    setPendingAdds((current) => (
      current.includes(zipCode)
        ? current.filter((value) => value !== zipCode)
        : sortZipCodes([...current, zipCode])
    ));
    setPendingRemovals((current) => current.filter((value) => value !== zipCode));
  }, [onSelectZip]);

  const handleLassoResult = useCallback((polygon) => {
    const map = mapRef.current;
    if (!map || !canInteractRef.current) return;

    // Query features within the polygon's screen-space bounding box
    const polyBbox = turf.bbox(polygon);
    const sw = map.project([polyBbox[0], polyBbox[1]]);
    const ne = map.project([polyBbox[2], polyBbox[3]]);
    const screenBbox = [
      [Math.min(sw.x, ne.x), Math.min(sw.y, ne.y)],
      [Math.max(sw.x, ne.x), Math.max(sw.y, ne.y)],
    ];

    const features = map.queryRenderedFeatures(screenBbox, { layers: [FILL_LAYER_ID] });
    const selected = new Set();

    for (const feature of features) {
      const zipCode = getZipFromFeature(feature);
      if (!zipCode || selected.has(zipCode)) continue;

      // Try intersection first (handles tile-clipped geometries)
      try {
        if (turf.booleanIntersects(feature, polygon)) {
          selected.add(zipCode);
          continue;
        }
      } catch (_) {}

      // Fallback: centroid check
      try {
        const center = turf.centroid(feature);
        if (turf.booleanPointInPolygon(center, polygon)) {
          selected.add(zipCode);
        }
      } catch (_) {}
    }

    if (!selected.size) return;

    const activeSet = new Set(companyZipCodesRef.current);
    const newAdds = [...selected].filter((z) => !activeSet.has(z));
    if (newAdds.length) {
      setPendingAdds((current) => sortZipCodes([...new Set([...current, ...newAdds])]));
    }
  }, []);

  const clearLassoOverlay = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    const src = map.getSource('lasso-source');
    if (src) {
      src.setData({ type: 'Feature', geometry: { type: 'Polygon', coordinates: [[]] } });
    }
  }, []);

  const toggleLasso = useCallback(() => {
    setLassoActive((active) => {
      if (active) {
        clearLassoOverlay();
        lassoPointsRef.current = [];
        lassoDrawingRef.current = false;
        lassoActiveRef.current = false;
        const map = mapRef.current;
        if (map) {
          map.dragPan.enable();
          map.getCanvas().style.cursor = '';
        }
        return false;
      }
      lassoActiveRef.current = true;
      const map = mapRef.current;
      if (map) {
        map.getCanvas().style.cursor = 'crosshair';
      }
      return true;
    });
  }, [clearLassoOverlay]);

  // ── Map init ──
  useEffect(() => {
    if (!mapContainerRef.current || !mapConfig || mapRef.current) {
      return;
    }

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/mapbox/light-v11',
      center: [-98.5795, 39.8283],
      zoom: 3.6,
    });
    map.addControl(new mapboxgl.NavigationControl(), 'top-right');
    mapRef.current = map;

    map.on('load', () => {
      map.addSource(SOURCE_ID, {
        type: 'vector',
        url: `mapbox://${mapConfig.mapbox_url}`,
      });

      map.addLayer({
        id: FILL_LAYER_ID,
        type: 'fill',
        source: SOURCE_ID,
        'source-layer': mapConfig.mapbox_layer,
        paint: {
          'fill-color': '#e7e2d9',
          'fill-opacity': 0.18,
        },
      });

      map.addLayer({
        id: OUTLINE_LAYER_ID,
        type: 'line',
        source: SOURCE_ID,
        'source-layer': mapConfig.mapbox_layer,
        paint: {
          'line-color': '#b7b0a6',
          'line-width': 0.45,
        },
      });

      map.addLayer({
        id: 'coverage-zips-label',
        type: 'symbol',
        source: SOURCE_ID,
        'source-layer': mapConfig.mapbox_layer,
        minzoom: 7,
        layout: {
          'text-field': ZIP_EXPR,
          'text-size': ['interpolate', ['linear'], ['zoom'], 7, 10, 10, 14, 13, 18],
          'text-font': ['DIN Pro Bold', 'Arial Unicode MS Bold'],
          'text-allow-overlap': false,
          'text-ignore-placement': false,
          'text-padding': 2,
          'symbol-placement': 'point',
        },
        paint: {
          'text-color': '#1e293b',
          'text-halo-color': '#ffffff',
          'text-halo-width': 2,
          'text-opacity': ['interpolate', ['linear'], ['zoom'], 7, 0.5, 9, 0.85, 11, 1],
        },
      });

      // Lasso overlay source + layers
      map.addSource('lasso-source', {
        type: 'geojson',
        data: { type: 'Feature', geometry: { type: 'Polygon', coordinates: [[]] } },
      });
      map.addLayer({
        id: 'lasso-fill',
        type: 'fill',
        source: 'lasso-source',
        paint: { 'fill-color': '#e26313', 'fill-opacity': 0.12 },
      });
      map.addLayer({
        id: 'lasso-line',
        type: 'line',
        source: 'lasso-source',
        paint: { 'line-color': '#e26313', 'line-width': 2, 'line-dasharray': [3, 2] },
      });

      updateMapStylingRef.current();

      map.on('mouseenter', FILL_LAYER_ID, () => {
        if (!lassoActiveRef.current) {
          map.getCanvas().style.cursor = 'pointer';
        }
      });
      map.on('mouseleave', FILL_LAYER_ID, () => {
        if (!lassoActiveRef.current) {
          map.getCanvas().style.cursor = '';
        }
      });
      map.on('click', FILL_LAYER_ID, (event) => {
        if (lassoActiveRef.current) return;
        const feature = event.features?.[0];
        const zipCode = getZipFromFeature(feature);
        if (!zipCode) {
          return;
        }
        toggleZipSelection(zipCode);
      });

      setMapReady(true);
    });

    return () => {
      map.remove();
      mapRef.current = null;
      setMapReady(false);
    };
  }, [mapConfig, toggleZipSelection]);

  // Freeform lasso: mouse events on canvas
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !lassoActive) return;

    const canvas = map.getCanvas();

    const onMouseDown = (e) => {
      if (e.button !== 0) return; // left click only
      e.preventDefault();
      lassoDrawingRef.current = true;
      map.dragPan.disable();
      const lngLat = map.unproject([e.offsetX, e.offsetY]);
      lassoPointsRef.current = [[lngLat.lng, lngLat.lat]];
    };

    const onMouseMove = (e) => {
      if (!lassoDrawingRef.current) return;
      const lngLat = map.unproject([e.offsetX, e.offsetY]);
      lassoPointsRef.current.push([lngLat.lng, lngLat.lat]);

      // Update lasso overlay
      const coords = [...lassoPointsRef.current, lassoPointsRef.current[0]];
      const src = map.getSource('lasso-source');
      if (src) {
        src.setData({
          type: 'Feature',
          geometry: { type: 'Polygon', coordinates: [coords] },
        });
      }
    };

    const onMouseUp = () => {
      if (!lassoDrawingRef.current) return;
      lassoDrawingRef.current = false;

      const points = lassoPointsRef.current;
      if (points.length >= 3) {
        const closed = [...points, points[0]];
        const polygon = {
          type: 'Feature',
          geometry: { type: 'Polygon', coordinates: [closed] },
        };
        handleLassoResult(polygon);
      }

      // Clean up: clear overlay, reset state, re-enable drag
      lassoPointsRef.current = [];
      lassoActiveRef.current = false;
      clearLassoOverlay();
      setLassoActive(false);
      map.dragPan.enable();
      canvas.style.cursor = '';
    };

    canvas.addEventListener('mousedown', onMouseDown);
    canvas.addEventListener('mousemove', onMouseMove);
    canvas.addEventListener('mouseup', onMouseUp);

    return () => {
      canvas.removeEventListener('mousedown', onMouseDown);
      canvas.removeEventListener('mousemove', onMouseMove);
      canvas.removeEventListener('mouseup', onMouseUp);
      lassoDrawingRef.current = false;
      lassoPointsRef.current = [];
    };
  }, [mapReady, lassoActive, handleLassoResult, clearLassoOverlay]);

  useEffect(() => {
    if (!mapReady) {
      return;
    }
    updateMapStyling();
  }, [mapReady, updateMapStyling]);

  // Fit map to coverage on initial load only
  const hasInitialFitRef = useRef(false);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || hasInitialFitRef.current) {
      return;
    }

    const focusZipCodes = allCompanyZipCodes.length ? allCompanyZipCodes : [];
    if (focusZipCodes.length && fitCoverageToContexts(focusZipCodes)) {
      hasInitialFitRef.current = true;
      return;
    }

    const targetContext =
      companyZipContextMap[companyZipCodes[0]] ||
      companyZipContextMap[allCompanyZipCodes[0]];

    if (targetContext) {
      focusOnZipContext(targetContext, 9.4);
      hasInitialFitRef.current = true;
    }
  }, [
    allCompanyZipCodes,
    companyZipCodes,
    companyZipContextMap,
    fitCoverageToContexts,
    focusOnZipContext,
    mapReady,
  ]);

  // ── Handlers ──
  const parseZipInput = () => {
    const rawZips = searchZip.split(/[\s,]+/).map(normalizeZip).filter(Boolean);
    return [...new Set(rawZips)];
  };

  const handleSearchZip = async (event) => {
    event.preventDefault();
    setError('');
    setSearchResult(null);

    const uniqueZips = parseZipInput();
    if (uniqueZips.length === 0) {
      setError('Enter a valid 5-digit ZIP code.');
      return;
    }

    const alreadyActive = new Set(companyZipCodes);
    const newZips = uniqueZips.filter((zipCode) => !alreadyActive.has(zipCode) && !pendingAdds.includes(zipCode));
    if (newZips.length === 0) {
      setError(`All ${uniqueZips.length} ZIPs are already active or requested.`);
      return;
    }

    setIsSearching(true);
    try {
      const response = await api.get('/api/zip-context', {
        params: { zip_code: uniqueZips[0] },
      });
      setSearchResult(response.data);
      focusOnZipContext(response.data, 10.2);
      setPendingAdds((current) => sortZipCodes([...current, ...newZips]));
      setSearchZip('');
      if (onSelectZip && accessibleZipCodesRef.current.includes(response.data.zip_code)) {
        onSelectZip(response.data.zip_code);
      }
    } catch (requestError) {
      setError(requestError?.response?.data?.error || 'ZIP code not found.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchToggle = () => {
    if (!searchResult || !canInteract) {
      return;
    }
    toggleZipSelection(searchResult.zip_code);
  };

  const refreshBootstrap = useCallback(async (overrideCompanyId = selectedCompanyId) => {
    const params = {};
    if (clientId) {
      params.client_id = clientId;
    }
    if (overrideCompanyId) {
      params.company_id = overrideCompanyId;
    }
    const response = await api.get('/api/session-bootstrap', { params });
    if (onCompanySetupComplete) {
      onCompanySetupComplete(response.data);
    }
    return response.data;
  }, [clientId, onCompanySetupComplete, selectedCompanyId]);

  const handleCompanySelectionChange = async (event) => {
    const nextCompanyId = event.target.value;
    if (!nextCompanyId || String(nextCompanyId) === String(selectedCompanyId)) {
      return;
    }

    setIsSwitchingCompany(true);
    setError('');
    try {
      await refreshBootstrap(nextCompanyId);
    } catch (requestError) {
      setError(requestError?.response?.data?.error || 'Failed to switch company context.');
    } finally {
      setIsSwitchingCompany(false);
    }
  };

  const handleAddMember = async (event) => {
    event.preventDefault();
    const email = normalizeEmail(memberForm.email);
    if (!email) {
      setMemberError('Enter a valid email address.');
      return;
    }

    const emailAlreadyAttached = companyMembers.some((member) => normalizeEmail(member.email) === email);
    const emailAlreadyPending = pendingMemberAdds.some((member) => normalizeEmail(member.email) === email);
    if (emailAlreadyAttached) {
      setMemberError('That email is already attached to this company. Undo any staged removal instead.');
      return;
    }
    if (emailAlreadyPending) {
      setMemberError('That email is already staged to be added.');
      return;
    }

    setMemberError('');
    setPendingMemberAdds((current) => [
      ...current,
      {
        email,
        role: memberForm.role,
        title: null,
      },
    ]);
    setPendingMemberRemovals((current) => current.filter((member) => normalizeEmail(member.email) !== email));
    setMemberForm({ email: '', role: 'member' });
    setShowTeamSection(true);
  };

  const handleMemberRoleChange = async (memberId, role) => {
    setMemberError('');
    setMemberActionKey(`role-${memberId}`);
    try {
      const response = await api.post('/api/company/members/update-role', {
        member_id: memberId,
        role,
        company_id: selectedCompanyId,
        client_id: clientId || undefined,
      });
      if (onCompanySetupComplete) {
        onCompanySetupComplete(response.data);
      }
    } catch (requestError) {
      setMemberError(requestError?.response?.data?.error || 'Failed to update member role.');
    } finally {
      setMemberActionKey('');
    }
  };

  const handleRemoveMember = async (memberId) => {
    setMemberError('');
    const pendingAddedIndex = pendingMemberAdds.findIndex((member) => `pending:${member.email}` === String(memberId));
    if (pendingAddedIndex >= 0) {
      setPendingMemberAdds((current) => current.filter((_, index) => index !== pendingAddedIndex));
      return;
    }

    const existingMember = companyMembers.find((member) => String(member.id) === String(memberId));
    if (!existingMember) {
      setMemberError('Company member not found.');
      return;
    }
    if (!window.confirm('Add this member removal to your request? They will keep access until RealTorch confirms the change with you.')) {
      return;
    }
    setPendingMemberRemovals((current) => (
      current.some((member) => member.id === existingMember.id)
        ? current
        : [...current, existingMember]
    ));
  };

  const handleOpenReviewStep = () => {
    if (!hasPendingChanges) {
      return;
    }
    setError('');
    setStep('review');
  };

  const handleSubmitCoverageRequest = async (overrideCompanyId = selectedCompanyId) => {
    const resolvedCompanyId = (
      overrideCompanyId
      && typeof overrideCompanyId !== 'object'
      && typeof overrideCompanyId !== 'function'
    )
      ? overrideCompanyId
      : selectedCompanyId;
    if (!hasPendingChanges) {
      setError('Select at least one ZIP or team change before submitting a request.');
      return;
    }
    setRequestState('submitting');
    setError('');
    try {
      const response = await api.post('/api/company/coverage-change-request', {
        add_zip_codes: pendingAdds,
        deactivate_zip_codes: pendingRemovals,
        add_members: pendingMemberAdds,
        remove_member_ids: pendingMemberRemovals.map((member) => member.id),
        note: requestNote.trim() || undefined,
        company_id: resolvedCompanyId || undefined,
        client_id: clientId || undefined,
      });
      setRequestResult(response.data || {});
      setRequestState('complete');
    } catch (requestError) {
      setError(
        requestError?.response?.data?.error
        || requestError?.message
        || 'Failed to submit your coverage request.'
      );
      setRequestState('idle');
    }
  };

  const handleCompanySetupThenRequest = async (bootstrapPayload) => {
    if (onCompanySetupComplete) {
      onCompanySetupComplete(bootstrapPayload);
    }
    const nextCompanyId = bootstrapPayload?.company_id || bootstrapPayload?.selected_company_id;
    if (!nextCompanyId) {
      return;
    }
    await handleSubmitCoverageRequest(nextCompanyId);
  };

  const handleReset = () => {
    setPendingAdds([]);
    setPendingRemovals([]);
    setPendingMemberAdds([]);
    setPendingMemberRemovals([]);
    setError('');
    setStep('select');
    setRequestState('idle');
    setRequestNote('');
    setRequestResult(null);
  };

  const renderCompanyMembers = () => (
    <section className="data-coverage-section data-coverage-team-card">
      <div className="data-coverage-section-head data-coverage-team-head">
        <div>
          <h4>Team access</h4>
          <p>{currentCompanyMemberCount} member{currentCompanyMemberCount === 1 ? '' : 's'} currently attached to this company</p>
        </div>
      </div>

      {(visibleCompanyMembers.length || pendingMemberAdds.length) ? (
        <div className="data-coverage-member-list">
          {visibleCompanyMembers.map((member) => {
            const hasRealName = member.name && !['member', 'admin', 'owner'].includes(member.name.toLowerCase());
            const displayName = hasRealName ? member.name : (member.email || member.name || `User ${member.user_id}`);
            const isOwner = member.role === 'owner';
            const isSelf = String(member.user_id) === String(coverageState?.actor_user_id);
            const nextRole = member.role === 'member' ? 'admin' : 'member';
            const actionLabel = member.role === 'member' ? 'Make admin' : 'Make member';

            return (
              <div key={member.id} className="data-coverage-member-row">
                <div className="data-coverage-member-copy">
                  <strong>{displayName}{isSelf ? ' (you)' : ''}</strong>
                  <span>{member.email || (hasRealName ? '' : 'Invitation pending')}</span>
                </div>
                <span className={`data-coverage-status-badge is-${member.role === 'owner' ? 'active' : member.role === 'admin' ? 'demo' : 'inactive'}`}>
                  {member.role}
                </span>
                {canManageCompanyMembers && !isSelf ? (
                  <div className="data-coverage-member-actions">
                    {!isOwner ? (
                      <button
                        type="button"
                        className="data-coverage-chip-action is-secondary"
                        onClick={() => handleMemberRoleChange(member.id, nextRole)}
                        disabled={memberActionKey === `role-${member.id}` || memberActionKey === `remove-${member.id}`}
                      >
                        {memberActionKey === `role-${member.id}` ? 'Saving...' : actionLabel}
                      </button>
                    ) : null}
                    <button
                        type="button"
                        className="data-coverage-chip-action is-danger"
                        onClick={() => handleRemoveMember(member.id)}
                        disabled={memberActionKey === `role-${member.id}`}
                      >
                        Stage removal
                      </button>
                    </div>
                  ) : null}
                </div>
              );
            })}
          {pendingMemberAdds.map((member) => (
            <div key={`pending:${member.email}`} className="data-coverage-member-row is-pending">
              <div className="data-coverage-member-copy">
                <strong>{member.email}</strong>
                <span>Included in this change request</span>
              </div>
              <div className="data-coverage-member-actions">
                <button
                  type="button"
                  className="data-coverage-chip-action is-warning"
                  onClick={() => handleRemoveMember(`pending:${member.email}`)}
                >
                  Undo
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="data-coverage-empty-state">No company members yet.</p>
      )}

      {(pendingMemberAdds.length > 0 || pendingMemberRemovals.length > 0) ? (
        <div className="data-coverage-pending-summary">
          {pendingMemberAdds.length > 0 ? (
            <div className="data-coverage-pending-row is-add">
              <span>Requested seat additions</span>
              <div className="data-coverage-pending-tags">
                {pendingMemberAdds.map((member) => (
                  <button
                    key={`tag:${member.email}`}
                    type="button"
                    className="data-coverage-pending-tag"
                    onClick={() => handleRemoveMember(`pending:${member.email}`)}
                  >
                    {member.email} &times;
                  </button>
                ))}
              </div>
            </div>
          ) : null}
          {pendingMemberRemovals.length > 0 ? (
            <div className="data-coverage-pending-row is-remove">
              <span>Pending seat removals</span>
              <div className="data-coverage-pending-tags">
                {pendingMemberRemovals.map((member) => (
                  <button
                    key={`remove:${member.id}`}
                    type="button"
                    className="data-coverage-pending-tag"
                    onClick={() => setPendingMemberRemovals((current) => current.filter((currentMember) => currentMember.id !== member.id))}
                  >
                    {(member.email || member.name || `User ${member.user_id}`)} &times;
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {canManageCompanyMembers ? (
        <form className="data-coverage-seat-form" onSubmit={handleAddMember}>
          <div className="data-coverage-form-intro">
            <strong>Add teammate</strong>
            <span>Invite members to share access to your coverage data.</span>
          </div>
          <label className="data-coverage-seat-form-email">
            <span>Email address</span>
            <input
              type="email"
              value={memberForm.email}
              onChange={(event) => setMemberForm((current) => ({ ...current, email: event.target.value }))}
              placeholder="teammate@company.com"
            />
          </label>
          <div className="data-coverage-seat-form-row">
            <label>
              <span>Role</span>
              <select
                value={memberForm.role}
                onChange={(event) => setMemberForm((current) => ({ ...current, role: event.target.value }))}
              >
                <option value="member">Member</option>
                <option value="admin">Admin</option>
              </select>
            </label>
            <button className="data-coverage-chip-action is-primary data-coverage-seat-form-submit" type="submit" disabled={memberActionKey === 'add-member'}>
              {memberActionKey === 'add-member' ? 'Adding...' : 'Add member'}
            </button>
          </div>
        </form>
      ) : (
        <p className="data-coverage-empty-state">Only company owners and admins can request coverage and member access changes.</p>
      )}

      {memberError ? <div className="data-coverage-seat-status">{memberError}</div> : null}
    </section>
  );

  // ── Render: Stepper ──
  const renderStepper = () => (
    <div className="data-coverage-stepper">
      <button
        type="button"
        className={`data-coverage-step ${step === 'select' ? 'active' : ''} ${step === 'review' ? 'completed' : ''}`}
        onClick={() => step === 'review' && setStep('select')}
        disabled={step === 'select'}
      >
        <span className="data-coverage-step-num">{step === 'review' ? '\u2713' : '1'}</span>
        Choose ZIPs
      </button>
      <div className="data-coverage-step-line" />
      <button
        type="button"
        className={`data-coverage-step ${step === 'review' ? 'active' : ''}`}
        disabled={!hasPendingChanges || step === 'review'}
      >
        <span className="data-coverage-step-num">2</span>
        Review &amp; request
      </button>
    </div>
  );

  // ── Render: Step 1 (Select) ──
  const renderSelectStep = () => (
    <>
      <div className="data-coverage-toolbar" style={step !== 'select' ? { display: 'none' } : undefined}>
        <div>
          <h3>Build your coverage request</h3>
          <p>
            {isViewOnly
              ? 'Viewing coverage for this account (read only).'
              : 'Search for one or more ZIPs, click the map, or use the lasso tool. Nothing changes until RealTorch confirms it with you.'}
          </p>
        </div>

        <div className="data-coverage-toolbar-actions">
          {companyMemberships.length > 1 ? (
            <label className="data-coverage-company-select">
              <span>Company</span>
              <select
                value={selectedCompanyId}
                onChange={handleCompanySelectionChange}
                disabled={isSwitchingCompany || requestState === 'submitting'}
              >
                {companyMemberships.map((company) => (
                  <option key={company.company_id} value={company.company_id}>
                    {company.company_name} ({company.role})
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {!isViewOnly ? (
            <form className="data-coverage-search" onSubmit={handleSearchZip}>
              <input
                type="text"
                value={searchZip}
                onChange={(event) => setSearchZip(event.target.value)}
                placeholder="Add ZIPs — e.g. 76008, 76033, 76035"
              />
              <button type="submit" disabled={isSearching}>
                {isSearching ? 'Adding...' : 'Add ZIPs'}
              </button>
            </form>
          ) : null}
        </div>
      </div>

      {/* Keep map mounted but hidden during review so it doesn't re-initialize */}
      <div className="data-coverage-body" style={step !== 'select' ? { display: 'none' } : undefined}>
        <div className="data-coverage-map-shell">
          {mapError ? <div className="data-coverage-map-state">{mapError}</div> : null}
          <div className="data-coverage-map-hint">
            {isViewOnly
              ? 'Read-only view.'
              : lassoActive
                ? 'Click and drag to draw a selection area.'
                : 'Click a ZIP to add or remove it from this request.'}
          </div>
          {!isViewOnly && mapReady ? (
            <button
              type="button"
              className={`data-coverage-lasso-btn ${lassoActive ? 'active' : ''}`}
              onClick={toggleLasso}
              title={lassoActive ? 'Cancel lasso' : 'Click and drag to select multiple ZIPs'}
            >
              {lassoActive ? 'Cancel' : 'Lasso'}
            </button>
          ) : null}
          <div ref={mapContainerRef} className="data-coverage-map" />
        </div>

        <aside className="data-coverage-sidebar">
          <div className="data-coverage-summary">
            <strong>{companyZipCodes.length}</strong> active ZIP{companyZipCodes.length === 1 ? '' : 's'}
            {hasPendingChanges && (
              <span className="data-coverage-summary-delta">
                &rarr; {projectedZipCodes.length} after changes
              </span>
            )}
          </div>

          {searchResult ? (
            <div className="data-coverage-search-result">
              <div>
                <strong>{searchResult.zip_code}</strong>
                <p>
                  {searchResult.city}, {searchResult.state_abbreviation}
                </p>
              </div>
              {canInteract ? (
                <button
                  type="button"
                  className={
                    pendingAdds.includes(searchResult.zip_code) || pendingRemovals.includes(searchResult.zip_code)
                      ? 'is-warning'
                      : companyZipStatusMap[searchResult.zip_code] === COVERAGE_STATUS_ACTIVE
                        ? 'is-secondary'
                        : 'is-primary'
                  }
                  onClick={handleSearchToggle}
                >
                  {pendingAdds.includes(searchResult.zip_code) || pendingRemovals.includes(searchResult.zip_code)
                    ? 'Undo'
                    : companyZipStatusMap[searchResult.zip_code] === COVERAGE_STATUS_ACTIVE
                      ? 'Deactivate'
                      : companyZipStatusMap[searchResult.zip_code] === COVERAGE_STATUS_DEMO
                        ? 'Activate'
                        : companyZipStatusMap[searchResult.zip_code] === COVERAGE_STATUS_INACTIVE
                          ? 'Reactivate'
                          : 'Add'}
                </button>
              ) : null}
            </div>
          ) : null}

          {error && step === 'select' ? <div className="data-coverage-error">{error}</div> : null}

          {!isViewOnly && hasPendingChanges ? (
            <div className="data-coverage-pending-summary">
              {pendingAdds.length > 0 && (
                <div className="data-coverage-pending-row is-add">
                  <span>+{pendingAdds.length} requested to add</span>
                  <div className="data-coverage-pending-tags">
                    {pendingAdds.map((zipCode) => (
                      <button
                        key={zipCode}
                        type="button"
                        className="data-coverage-pending-tag"
                        onClick={() => setPendingAdds((current) => current.filter((v) => v !== zipCode))}
                        title="Remove from pending"
                      >
                        {zipCode} &times;
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {pendingRemovals.length > 0 && (
                <div className="data-coverage-pending-row is-remove">
                  <span>&minus;{pendingRemovals.length} requested to remove</span>
                  <div className="data-coverage-pending-tags">
                    {pendingRemovals.map((zipCode) => (
                      <button
                        key={zipCode}
                        type="button"
                        className="data-coverage-pending-tag"
                        onClick={() => setPendingRemovals((current) => current.filter((v) => v !== zipCode))}
                        title="Remove from pending"
                      >
                        {zipCode} &times;
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}

          {allCompanyZipCodes.length > 0 && (
            <button
              type="button"
              className="data-coverage-toggle-btn"
              onClick={() => {
                setShowTeamSection(false);
                setShowAllZips((v) => !v);
              }}
            >
              {showAllZips ? 'Hide' : 'View'} all {allCompanyZipCodes.length} ZIPs
              <span className={`data-coverage-toggle-arrow ${showAllZips ? 'open' : ''}`}>&#9662;</span>
            </button>
          )}

          {showAllZips && (
            <div className="data-coverage-zip-drawer">
              {allCompanyZipCodes.map((zipCode) => {
                const status = companyZipStatusMap[zipCode] || COVERAGE_STATUS_INACTIVE;
                const isPendingRemoval = pendingRemovals.includes(zipCode);
                const isPendingAdd = pendingAdds.includes(zipCode);
                let actionLabel = '';
                let actionClassName = 'data-coverage-chip-action';

                if (status === COVERAGE_STATUS_ACTIVE) {
                  actionLabel = isPendingRemoval ? 'Undo' : 'Request removal';
                  actionClassName = isPendingRemoval
                    ? 'data-coverage-chip-action is-warning'
                    : 'data-coverage-chip-action is-secondary';
                } else if (status === COVERAGE_STATUS_DEMO) {
                  actionLabel = isPendingAdd ? 'Undo' : 'Request access';
                  actionClassName = isPendingAdd
                    ? 'data-coverage-chip-action is-warning'
                    : 'data-coverage-chip-action is-primary';
                } else {
                  actionLabel = isPendingAdd ? 'Undo' : 'Request access';
                  actionClassName = isPendingAdd
                    ? 'data-coverage-chip-action is-warning'
                    : 'data-coverage-chip-action is-secondary';
                }

                return (
                  <div
                    key={zipCode}
                    className={[
                      'data-coverage-chip-row',
                      selectedZip === zipCode ? 'selected' : '',
                      isPendingRemoval ? 'pending-removal' : '',
                      isPendingAdd ? 'pending-add' : '',
                    ].filter(Boolean).join(' ')}
                  >
                    <button
                      type="button"
                      className="data-coverage-chip-main"
                      onClick={() => {
                        focusOnZipContext(companyZipContextMap[zipCode], 10);
                        if (onSelectZip && accessibleZipCodesRef.current.includes(zipCode)) {
                          onSelectZip(zipCode);
                        }
                      }}
                    >
                      {zipCode}
                    </button>
                    <span className={`data-coverage-status-label is-${status}`}>
                      {getCoverageStatusLabel(status)}
                    </span>
                    {canInteract ? (
                      <button
                        type="button"
                        className={actionClassName}
                        onClick={() => toggleZipSelection(zipCode)}
                      >
                        {actionLabel}
                      </button>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}

          {hasCompanyMembership && (
            <>
              <button
                type="button"
                className="data-coverage-toggle-btn"
                onClick={() => {
                  setShowAllZips(false);
                  setShowTeamSection((v) => !v);
                }}
              >
                Manage team ({projectedCompanyMemberCount})
                <span className={`data-coverage-toggle-arrow ${showTeamSection ? 'open' : ''}`}>&#9662;</span>
              </button>
              {showTeamSection && renderCompanyMembers()}
            </>
          )}

          {!isViewOnly ? (
            <div className="data-coverage-actions">
              <button
                type="button"
                className="secondary"
                onClick={handleReset}
                disabled={!hasPendingChanges}
              >
                Clear request
              </button>
              <button
                type="button"
                className="primary"
                onClick={handleOpenReviewStep}
                disabled={!hasPendingChanges}
              >
                {hasPendingChanges
                  ? 'Review request'
                  : 'Next'}
              </button>
            </div>
          ) : null}
        </aside>
      </div>
    </>
  );

  // ── Render: Step 2 (Review & Request) ──
  const renderReviewStep = () => {
    if (requestState === 'complete') {
      return (
        <div className="data-coverage-review">
          <div className="data-coverage-review-scroll">
            <div className="dcr-success">
              <div className="dcr-success-icon">&#10003;</div>
              <h3>Request sent</h3>
              <p>RealTorch will contact you to review and coordinate the requested changes.</p>
              {requestResult?.request_id ? <small>Request ID: {requestResult.request_id}</small> : null}
              <button type="button" className="primary" onClick={handleReset}>
                Done
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="data-coverage-review">
        <div className="data-coverage-review-scroll">
          <div className="dcr-request-grid">
            {/* ── Left column: Order summary + Company ── */}
            <div className="dcr-order-col">
              <h3 className="dcr-col-title">Requested changes</h3>

              {/* Line items */}
              <div className="dcr-line-items">
                {/* ZIP change details */}
                {pendingAdds.length > 0 && (
                  <div className="dcr-line-item">
                    <div className="dcr-line-item-header">
                      <div className="dcr-line-item-info">
                        <span className="dcr-line-item-badge dcr-badge-add">+{pendingAdds.length}</span>
                        <div>
                          <div className="dcr-line-item-name">
                            Adding {pendingAdds.length} ZIP{pendingAdds.length === 1 ? '' : ' codes'}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="dcr-zip-list">
                      {pendingAdds.map((zipCode) => (
                        <span key={zipCode} className="dcr-zip-tag dcr-zip-tag-add">
                          {zipCode}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {pendingRemovals.length > 0 && (
                  <div className="dcr-line-item">
                    <div className="dcr-line-item-header">
                      <div className="dcr-line-item-info">
                        <span className="dcr-line-item-badge dcr-badge-remove">&minus;{pendingRemovals.length}</span>
                        <div>
                          <div className="dcr-line-item-name">
                            Removing {pendingRemovals.length} ZIP{pendingRemovals.length === 1 ? '' : ' codes'}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="dcr-zip-list">
                      {pendingRemovals.map((zipCode) => (
                        <span key={zipCode} className="dcr-zip-tag dcr-zip-tag-remove">
                          {zipCode}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {pendingMemberAdds.length > 0 && (
                  <div className="dcr-line-item">
                    <div className="dcr-line-item-header">
                      <div className="dcr-line-item-info">
                        <span className="dcr-line-item-badge dcr-badge-add">Seat</span>
                        <div>
                          <div className="dcr-line-item-name">
                            Adding {pendingMemberAdds.length} team member{pendingMemberAdds.length === 1 ? '' : 's'}
                          </div>
                          <div className="dcr-line-item-desc">Access changes only after RealTorch confirms the request with you.</div>
                        </div>
                      </div>
                    </div>
                    <div className="dcr-zip-list">
                      {pendingMemberAdds.map((member) => (
                        <span key={`review-add:${member.email}`} className="dcr-zip-tag dcr-zip-tag-add">
                          {member.email}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {pendingMemberRemovals.length > 0 && (
                  <div className="dcr-line-item">
                    <div className="dcr-line-item-header">
                      <div className="dcr-line-item-info">
                        <span className="dcr-line-item-badge dcr-badge-remove">Seat</span>
                        <div>
                          <div className="dcr-line-item-name">
                            Removing {pendingMemberRemovals.length} team member{pendingMemberRemovals.length === 1 ? '' : 's'}
                          </div>
                          <div className="dcr-line-item-desc">Access changes only after RealTorch confirms the request with you.</div>
                        </div>
                      </div>
                    </div>
                    <div className="dcr-zip-list">
                      {pendingMemberRemovals.map((member) => (
                        <span key={`review-remove:${member.id}`} className="dcr-zip-tag dcr-zip-tag-remove">
                          {member.email || member.name || `User ${member.user_id}`}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Company info */}
              <div className="dcr-account-section dcr-company-section">
                <h3 className="dcr-col-title">Company</h3>
                {needsCompanySetup ? (
                  <div className="dcr-account-form-area">
                    <p className="dcr-account-helper">
                      Create your company profile before sending this coverage request.
                    </p>
                    <CompanySetupForm
                      user={coverageState?.user}
                      pendingJoinRequest={coverageState?.pending_join_request}
                      onComplete={handleCompanySetupThenRequest}
                      submitLabel="Create company & send request"
                    />
                  </div>
                ) : (
                  <div className="dcr-account-card">
                    <div className="dcr-account-card-row">
                      <span className="dcr-account-label">Company</span>
                      <span className="dcr-account-value">{coverageState?.company_name || 'Company'}</span>
                    </div>
                    {coverageState?.company_role && (
                      <div className="dcr-account-card-row">
                        <span className="dcr-account-label">Role</span>
                        <span className="dcr-account-value">
                          <span className="dcr-company-badge">{coverageState.company_role}</span>
                        </span>
                      </div>
                    )}
                    {coverageState?.company_member_count > 0 && (
                      <div className="dcr-account-card-row">
                        <span className="dcr-account-label">Members</span>
                        <span className="dcr-account-value">
                          {coverageState.company_member_count}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* ── Right column: request handoff ── */}
            <div className="dcr-payment-col dcr-request-col">
              <h3 className="dcr-col-title">Send request to RealTorch</h3>
              <div className="dcr-request-intro">
                <span className="dcr-request-icon">&#9993;</span>
                <div>
                  <strong>RealTorch will follow up</strong>
                  <p>We will review the ZIP and team changes and contact you to coordinate next steps before anything changes.</p>
                </div>
              </div>

              <label className="dcr-request-note">
                <span>Anything else we should know? <small>Optional</small></span>
                <textarea
                  value={requestNote}
                  onChange={(event) => setRequestNote(event.target.value)}
                  rows={6}
                  maxLength={2000}
                  placeholder="Timing, markets, preferred contact, or other context..."
                />
                <small>{requestNote.length}/2000</small>
              </label>

              <div className="dcr-request-next">
                <strong>What happens next</strong>
                <ol>
                  <li>RealTorch receives the full request by email.</li>
                  <li>We contact you to confirm the requested changes and next steps.</li>
                  <li>Your account changes only after that confirmation.</li>
                </ol>
              </div>

              {!needsCompanySetup && (
                <button
                  type="button"
                  className="primary dcr-payment-btn"
                  onClick={() => handleSubmitCoverageRequest()}
                  disabled={!hasPendingChanges || requestState === 'submitting'}
                >
                  {requestState === 'submitting' ? 'Sending request...' : 'Send request to RealTorch'}
                </button>
              )}
              <button type="button" className="dcr-payment-link" onClick={() => setStep('select')}>
                Back to ZIP selection
              </button>
            </div>
          </div>

          {error && step === 'review' ? <div className="data-coverage-error">{error}</div> : null}
        </div>
      </div>
    );
  };

  return (
    <div className="data-coverage">
      {!isViewOnly ? renderStepper() : null}
      {renderSelectStep()}
      {step === 'review' ? renderReviewStep() : null}
    </div>
  );
}
